/** @jsx jsx */
import React, { useEffect, useState, type ChangeEvent } from 'react';
import { css, jsx } from '@compiled/react';
import { Label } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import CalendarIcon from '@atlaskit/icon/glyph/calendar';
import { Box, xcss } from '@atlaskit/primitives';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import {
	type MessageDescriptorV2 as MessageDescriptor,
	useIntlV2 as useIntl,
} from '@atlassian/jira-intl';
import { DATE_FORMAT_SLASH, DATE_FORMAT_SLASH_PLACEHOLDER } from '../../../../common/constants';
import type { DatePickerProps } from '../../../../common/types';
import { formatDate } from '../../../../common/utils/dates';
import messages from './messages';
import ValidationMessage, { type ValidationMessageType } from './validation-message';

type ValidationMessageObject = {
	type: ValidationMessageType;
	message: MessageDescriptor;
};

type Props = {
	isInvalid?: boolean;
} & DatePickerProps & {
		label?: string;
		validation?: ValidationMessageObject;
		selected?: Date;
		testId?: string;
		onChange?: (value: string) => void;
	};

const DateInput = ({
	selected,
	testId,
	isInvalid = false,
	onChange,
	label = 'Date picker',
	validation,
}: Props) => {
	const { formatMessage } = useIntl();
	const [inputValue, setInputValue] = useState<string>('');

	useEffect(() => {
		if (selected) {
			setInputValue(formatDate(selected, DATE_FORMAT_SLASH));
		}
	}, [selected]);

	const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
		e.preventDefault();
		setInputValue(e.target.value);
		if (onChange) {
			onChange(e.target.value);
		}
	};

	return (
		<>
			<Label htmlFor="date-input-text-field">
				<Heading level="h200" as="span">
					{label}
				</Heading>
			</Label>
			<TextField
				id="date-input-text-field"
				testId={testId}
				onChange={changeHandler}
				placeholder={DATE_FORMAT_SLASH_PLACEHOLDER}
				isInvalid={isInvalid}
				value={inputValue}
				elemBeforeInput={
					<Box xcss={iconWrapperStyles}>
						<CalendarIcon label={formatMessage(messages.calendarIconLabel)} />
					</Box>
				}
			/>
			<div css={validationWrapperStyles}>
				{validation !== undefined && (
					<ValidationMessage
						testId={testId !== undefined ? `${testId}.validation-message` : undefined}
						type={validation.type}
					>
						{formatMessage(validation.message, { dateType: label })}
					</ValidationMessage>
				)}
			</div>
		</>
	);
};

const validationWrapperStyles = css({
	marginTop: token('space.050', '4px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 2.5}px`,
});

const iconWrapperStyles = xcss({
	paddingLeft: 'space.100',
});

export default DateInput;
